import React, { useState, useEffect } from "react";
import "./../../../css/Students.scss";
import { Link } from "react-router-dom";
import SearchBarComponent from "../SearchBar";
import PaginationComponent from "../Pagination";
import axios from 'axios';
import { IoEyeOutline, IoFilterOutline } from "react-icons/io5";

export default function Students() {
    const [students, setStudents] = useState([]);
    const [advisors, setAdvisors] = useState([]);
    const [advisorFilter, setAdvisorFilter] = useState({});
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const studentsPerPage = 10;
    const [totalStudents, setTotalStudents] = useState(0);

    const fetchAdvisors = async () => {
        try {
            const response = await axios.get('/api/students/advisor');
            const advisorsData = response.data.advisors || [];
            setAdvisors(advisorsData);
            const initialFilter = {};
            advisorsData.forEach((advisor) => {
                initialFilter[advisor] = true;
            });
            initialFilter['(Unassigned)'] = true;
            setAdvisorFilter(initialFilter);
        } catch (error) {
            console.error('Error getting advisors: ', error);
        }
    };

    const fetchPaginatedStudents = async (page, search = searchInput) => {
        try {
            const LIMIT = 10;
            const offset = page * LIMIT;

            const response = await axios.get('/api/students', {
                params: {
                    limit: LIMIT,
                    offset: offset,
                    advisorLNames: Object.keys(advisorFilter).filter((key) => advisorFilter[key]).join(","),
                    searchInput: search,
                    flaggedOnly: flaggedOnly,
                },
            });

            if (response.data && response.data.students) {
                console.log(response.data)
                setStudents(response.data.students);
                setTotalStudents(response.data.total);
            } else {
                setStudents([]);
                setTotalStudents(0);
            }
        } catch (error) {
            console.error("Error fetching paginated students: ", error);
        }
    };

    // Fetch advisors on load
    useEffect(() => {
        fetchAdvisors();

        // Set initial advisor filter
        const initialFilter = {};
        advisors.forEach((advisor) => {
            initialFilter[advisor] = true;
        });
        setAdvisorFilter(initialFilter);
    }, []);

    // Fetch students on page/search/filter change
    useEffect(() => {
        fetchPaginatedStudents(currentPage);
    }, [currentPage, advisorFilter, totalStudents, flaggedOnly]);

    const handleFlaggedOnlyChange = () => {
        setFlaggedOnly(!flaggedOnly);
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected;
        setCurrentPage(newPage);
        fetchPaginatedStudents(newPage);
    };

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
    };

    const handleSearch = () => {
        setCurrentPage(0);
        fetchPaginatedStudents(0);

    };

    const clearSearch = async () => {
        setSearchInput("");
        setCurrentPage(0);
        await fetchPaginatedStudents(0, "");
    };

    const toggleAdvisor = (advisor) => {
        setAdvisorFilter((prevFilter) => ({
            ...prevFilter,
            [advisor]: !prevFilter[advisor],
        }));
        setCurrentPage(0);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="page-body">
            {/* Header and Filter section */}
            <h1>Students</h1>

            {/* Students Table */}
            <div className="header">
                {/* Filter section */}
                <div className="filter-container">
                    {/* Dropdown for advisors filter */}
                    <div
                        className="dropdown"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                        <IoFilterOutline className="filter-icon" title="Filter by advisor" />
                        <div className="dropdown-content" style={{ display: dropdownOpen ? "block" : "none" }}>
                            <div key="sort-by-advisor" className="dropdown-item nowrap" onClick={(event) => event.stopPropagation()}>
                                Filter by advisor
                            </div>
                            {/* Checkboxes for each advisor */}
                            {advisors.map((advisor) => (
                                <div key={advisor} onClick={(event) => event.stopPropagation()}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={advisorFilter[advisor]}
                                            onChange={() => toggleAdvisor(advisor)}
                                        />
                                        {advisor}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <label className="flagged-only-checkbox">
                    <input
                        type="checkbox"
                        checked={flaggedOnly}
                        onChange={handleFlaggedOnlyChange}
                    />
                    Flagged P-91 Students
                </label>
                {/* Search Bar */}
                <SearchBarComponent
                    searchInput={searchInput}
                    onSearchChange={handleSearchChange}
                    onSearch={handleSearch}
                    onClearSearch={clearSearch}
                    onKeyPress={handleKeyPress}
                />
            </div>
            {students.length > 0 ? (
                <div className="students-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Student ID</th>
                                <th>Student Name</th>
                                <th>E-mail</th>
                                <th>Admit Term</th>
                                <th>Graduation Term</th>
                                <th>Advisor Last Name</th>
                                <th>Overall GPA</th>
                                <th>In-Major GPA</th>
                                <th>Needs For Graduation</th>
                                <th>More Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Mapping through paginated students */}
                            {students.map((student) => (
                                <tr key={student.id} className={student.hasViolation == 1 ? "tagged-row" : ""}>
                                    <td>{student.id}</td>
                                    <td className="nowrap">{student.name}</td>
                                    <td>{student.email}</td>
                                    <td>{student.admit_term}</td>
                                    <td>{student.completion_term_code}</td>
                                    <td>{student.advisor_lname}</td>
                                    <td>
                                        {student.overall_gpa ? student.overall_gpa.toFixed(2) : 0}
                                    </td>
                                    <td>
                                        {student.in_major_gpa ? student.in_major_gpa.toFixed(2): 0}
                                    </td>
                                    <td>
                                        <ul className="class-list">
                                            {JSON.parse(student.missing_courses)?.map((course, index) => (
                                                <li key={index}>{course}</li>
                                            ))}
                                        </ul>
                                    </td>
                                    <td>
                                        <Link to={`/students/${student.id}`}>
                                            <IoEyeOutline title="View student information" />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <PaginationComponent
                        pageCount={Math.ceil(totalStudents / studentsPerPage)}
                        onPageChange={handlePageChange}
                        currentPage={currentPage}
                    />
                </div>
            ) : (
                <label className="no-students-message">No students found.</label>
            )}
        </div>
    );
}
